/**
 * Headless DOM Session
 *
 * One shared Chromium session for offline substrate capture and locator
 * verification. Substrate capture and verification MUST share one rendering
 * environment — viewport, screen, UA — or responsive/UA-dependent DOM
 * differences shift the aria-ref table between captures. Centralizing the
 * lifecycle here guarantees that invariant and halves the per-job Chromium
 * footprint versus each consumer owning a browser.
 *
 * The environment matches the scenario suite exactly: Desktop Chrome
 * descriptor + load-event hydration (`waitForTraceDomHydration`).
 *
 * Defense-in-depth: all http(s) requests are aborted on the context. Pages
 * are trace-reconstructed customer HTML loaded via `file://`; Chromium
 * already blocks file→file fetch/XHR by default, and the abort rule removes
 * the remaining network egress surface for hostile page scripts.
 *
 * @module optimizer/headless-session
 */

import { chromium, devices, type Browser, type BrowserContext, type Page } from 'playwright';

/**
 * Wall-clock cap on launching Chromium + opening the shared context. Unlike
 * `SubstrateMaterializer`'s own `timeoutMs` (which only bounds trace
 * reconstruction + substrate capture, AFTER a page already exists — see
 * `substrate-materializer.ts`), nothing previously bounded THIS launch step.
 * A wedged/starved headless Chromium launch (browser-slot contention,
 * container resource pressure) would hang here forever, invisible to every
 * caller's own timeout because `session.page()` is awaited before those
 * callers' timed sections begin. Root-caused during the R4 cache-convergence
 * harness fix (docs/project/cache-convergence-agent-ledger.md) after a
 * `materialize_substrate` call outlived its caller's watchdog with a
 * "browser-concurrency resolution" log line immediately before the abort.
 */
const LAUNCH_TIMEOUT_MS = 30_000;

class HeadlessLaunchTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'HeadlessLaunchTimeoutError';
  }
}

async function withLaunchTimeout<T>(promise: Promise<T>, description: string): Promise<T> {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeout = setTimeout(() => {
      reject(
        new HeadlessLaunchTimeoutError(`${description} timed out after ${LAUNCH_TIMEOUT_MS}ms`)
      );
    }, LAUNCH_TIMEOUT_MS);
  });
  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}

export class HeadlessDomSession {
  private browser: Browser | null = null;
  private context: BrowserContext | null = null;

  /**
   * Memoized in-flight init so CONCURRENT `page()` calls share one Chromium
   * launch. Without it, parallel first-callers each pass the `!this.context`
   * check and launch their own browser — the losers' processes leak and the
   * simultaneous launches contend hard enough on a loaded host to trip
   * `LAUNCH_TIMEOUT_MS` for everyone (cache-convergence ledger R7).
   */
  private initPromise: Promise<BrowserContext> | null = null;

  private async init(): Promise<BrowserContext> {
    // Atomic init: never leave a launched browser without a context — a
    // half-open state would leak the process (close() nulls both) and make
    // the `context` access below unsound.
    const browser = await withLaunchTimeout(chromium.launch({ headless: true }), 'Chromium launch');
    try {
      const context = await withLaunchTimeout(
        browser.newContext({ ...devices['Desktop Chrome'] }),
        'Chromium context creation'
      );
      await context.route(/^https?:/, (route) => route.abort());
      this.browser = browser;
      this.context = context;
      return context;
    } catch (err) {
      await browser.close().catch(() => undefined);
      throw err;
    }
  }

  async page(): Promise<Page> {
    if (this.context) return this.context.newPage();
    this.initPromise ??= this.init().catch((err: unknown) => {
      // A failed launch must not poison the session forever — clear the memo
      // so a later call can retry.
      this.initPromise = null;
      throw err;
    });
    const context = await this.initPromise;
    return context.newPage();
  }

  async close(): Promise<void> {
    await this.context?.close().catch(() => undefined);
    await this.browser?.close().catch(() => undefined);
    this.context = null;
    this.browser = null;
    this.initPromise = null;
  }
}
